'use client';

import { useTranslations } from 'next-intl';
import { z } from 'zod';
import { Button } from '@/components/Button';
import { useAppForm } from '@/lib/form/hook';

// Required in Strapi with a default, so it always arrives — no FE fallback
// (10.03.04.04 § CMS Component Schema).
export interface BookProjectFormResolved {
  targetBaseUrl: string;
}

/*
 * The form measure (70.14-Widget-Width § 5): narrower than the page on
 * purpose, because a form is read and filled rather than scanned — see
 * 10.03.04.01, which treats the form container width as its own property.
 * Only the measure; the gutter comes from the SlotRenderer wrapper.
 */
const containerClassName = 'mx-auto max-w-4xl py-8';

// Trailing slashes are trimmed so `…de` and `…de/` behave the same.
function buildBookProjectUrl(baseUrl: string, code: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/${encodeURIComponent(code)}`;
}

export function BookProjectFormComponent({
  resolved,
}: {
  resolved: BookProjectFormResolved;
}) {
  const t = useTranslations('bookProject');

  // Built inside the component, not at module level: the messages come from
  // the `t()` hook.
  const codeSchema = z
    .string()
    .trim()
    .min(1, t('codeRequired'))
    .regex(/^[A-Za-z0-9]+$/, t('codeInvalid'));

  const form = useAppForm({
    defaultValues: { code: '' },
    onSubmit: ({ value }) => {
      window.location.assign(
        buildBookProjectUrl(
          resolved.targetBaseUrl,
          codeSchema.parse(value.code),
        ),
      );
    },
  });

  return (
    <div className={containerClassName}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        noValidate
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div className="md:grow">
            {/* No `onBlur` validator: the field must not turn red while the
                visitor is still typing (10.03.04.04 AC 3). */}
            <form.AppField name="code" validators={{ onSubmit: codeSchema }}>
              {(field) => <field.TextField label={t('codeLabel')} required />}
            </form.AppField>
          </div>

          <Button size="md" type="submit" className="mx-auto md:mx-0">
            {t('submitButton')}
          </Button>
        </div>
      </form>
    </div>
  );
}
